import { useState } from "react";
import clsx from "clsx";
import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import { Account, Order } from "@app/types";
import { BootImageUnit, PathnameUrl } from "@app/constants";
import { formatMoneyVN, getOrderEndTime } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import { IcClock, IcMinus, IcPlus, IcWallet, IcX } from "../icons";
import { PcGlyph, btn, btnPrimary, btnLg, iconBtn } from "../ui";

interface BootImageModalProps {
  /** Đơn đang thuê (đã populate image kèm package) */
  order: Order;
  currentAccount: Account;
  /** Màu danh mục của cấu hình (khung và đèn vỏ case) */
  color?: string;
}

/** Nhãn, số giờ của 1 đơn vị (1 ngày = 24h, 1 tháng = 720h), các nút chọn nhanh và mức tối đa như bản thiết kế */
const UNITS = [
  { unit: BootImageUnit.HOUR, label: "Theo giờ", name: "giờ", hours: 1, quick: [1, 3, 6, 12], max: 720 },
  { unit: BootImageUnit.DAY, label: "Theo ngày", name: "ngày", hours: 24, quick: [1, 3, 7, 15], max: 90 },
  { unit: BootImageUnit.MONTH, label: "Theo tháng", name: "tháng", hours: 720, quick: [1, 3, 6, 12], max: 12 },
];

/**
 * Gia hạn máy. Trả về { rentalPeriod, unit } như bản cũ để trang gọi OrderService.extendOrder.
 * Thiếu tiền thì trả về { topup: số tiền còn thiếu } để trang mở Nạp tiền.
 * Giờ kết thúc hiện tại lấy từ order (end_time, hoặc time_boot + rental_period giờ).
 */
function BootImageModal({ portalDialogRef, portalData }: PortalDialogProps<BootImageModalProps>) {
  const t = useT();
  const order = portalData?.order;
  const pkg = order?.image?.package;

  // Mặc định cùng đơn vị đã thuê trước đó, như bản cũ
  const [unit, setUnit] = useState<BootImageUnit>(
    (order?.image?.unit as BootImageUnit) || BootImageUnit.HOUR
  );
  const [amount, setAmountState] = useState(1);

  if (!order || !pkg) return null;

  const current = UNITS.find((u) => u.unit === unit) || UNITS[0];
  const setAmount = (n: number) => setAmountState(Math.min(current.max, Math.max(1, Math.floor(n) || 1)));

  // Giá giống getMoneyPayment cũ: giá theo đơn vị × số lượng, trừ discount_pc
  const unitPrice = (u: BootImageUnit) =>
    u === BootImageUnit.HOUR ? pkg.price_per_hour : u === BootImageUnit.DAY ? pkg.price_per_day : pkg.price_per_month;
  const price = unitPrice(unit) * amount;
  const newPrice = price - (price * (portalData?.currentAccount?.discount_pc || 0)) / 100;
  const balance = portalData?.currentAccount?.balance || 0;
  const remain = balance - newPrice;

  const addedHours = amount * current.hours;
  const endTime = getOrderEndTime(order);
  const newEndTime = endTime.add(addedHours, "hour");

  // Quy tắc gói chỉ thuê theo ngày, như bản cũ
  const ruleError =
    pkg.user_day && unit === BootImageUnit.HOUR
      ? `Gói này không hỗ trợ thuê theo giờ, bạn cần thuê tối thiểu từ ${pkg.numbers_of_day} ngày`
      : pkg.user_day && unit === BootImageUnit.DAY && amount < pkg.numbers_of_day
      ? `Bạn cần thuê tối thiểu từ ${pkg.numbers_of_day} ngày`
      : "";

  const close = () => portalDialogRef.close();
  const row = "flex items-center justify-between gap-3";

  return (
    <Modal onCancel={close} buttonCancelInChildren={false}>
      <div className="flex w-[min(520px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start justify-between gap-3 border-b border-line px-6 pb-4 pt-5">
          <div>
            <h2 className="text-[19px] font-bold">{t("Gia hạn máy")}</h2>
            <p className="m-0 mt-0.5 text-[13px] text-muted">{order.image.name}</p>
          </div>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8")} aria-label={t("Đóng")} onClick={close}>
            <IcX className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          <div className="flex items-center gap-3 rounded-xl bg-surface-2 px-3 py-2.5">
            <span className="grid h-11 w-11 flex-none place-items-center rounded-[10px] bg-surface">
              <PcGlyph height={36} color={portalData?.color} />
            </span>
            <div className="min-w-0">
              <b className="block text-base">{order.image.name}</b>
              <span className="text-[13px] text-muted">
                {pkg.name} · {t("đang thuê")} {order.rental_period} {t("giờ")}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-muted">{t("Gia hạn theo")}</span>
            <div className="grid grid-cols-3 gap-2">
              {UNITS.map((u) => (
                <button
                  key={u.unit}
                  type="button"
                  onClick={() => {
                    setUnit(u.unit);
                    setAmountState(1);
                  }}
                  className={clsx(
                    "flex flex-col items-center rounded-xl border px-2 py-2 leading-tight",
                    unit === u.unit ? "border-brand bg-brand-soft text-brand" : "border-line text-fg"
                  )}
                >
                  <b className="text-sm">{t(u.label)}</b>
                  <small className={clsx("text-xs tabular-nums", unit === u.unit ? "text-brand" : "text-muted")}>
                    {formatMoneyVN(unitPrice(u.unit))}/{u.hours}h
                  </small>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold text-muted">
              {t("Thêm số")} {t(current.name)}
            </span>
            <div className="inline-flex items-center overflow-hidden rounded-[10px] border border-line">
              <button type="button" className="grid h-10 w-10 place-items-center text-muted hover:bg-surface-2" onClick={() => setAmount(amount - 1)}>
                <IcMinus className="h-4 w-4" />
              </button>
              <input
                type="number"
                min={1}
                max={current.max}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="h-10 w-12 border-x border-line bg-transparent text-center font-semibold tabular-nums outline-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button type="button" className="grid h-10 w-10 place-items-center text-muted hover:bg-surface-2" onClick={() => setAmount(amount + 1)}>
                <IcPlus className="h-4 w-4" />
              </button>
            </div>
            <div className="ml-auto flex gap-1.5">
              {current.quick.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setAmount(n)}
                  className={clsx(
                    "min-w-[36px] rounded-lg border px-2 py-1 text-[13px]",
                    amount === n ? "border-brand text-brand" : "border-line text-muted"
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-xl bg-surface-2 px-4 py-3 text-sm tabular-nums">
            <div className={row}>
              <span className="text-muted">{t("Kết thúc hiện tại")}</span>
              <b>{endTime.format("DD/MM/YYYY HH:mm")}</b>
            </div>
            <div className={row}>
              <span className="text-muted">{t("Kết thúc sau gia hạn")}</span>
              <b className="text-brand">{newEndTime.format("DD/MM/YYYY HH:mm")}</b>
            </div>
            <div className="text-muted">
              {t("Thêm")}: <b className="text-fg">{addedHours} {t("giờ")}</b>
              {unit !== BootImageUnit.HOUR && ` (${amount} ${t(current.name)})`}
            </div>
            <div className={row}>
              <span className="text-muted">{t("Tổng thời gian thuê")}</span>
              <b>
                {order.rental_period + addedHours} {t("giờ")}
              </b>
            </div>
            <div className={clsx(row, "border-t border-line pt-2")}>
              <b>{t("Thành tiền")}</b>
              <strong className="text-xl text-brand">{formatMoneyVN(newPrice)} VND</strong>
            </div>
          </div>

          {ruleError && <p className="m-0 text-[13px] font-medium text-warn">{ruleError}</p>}
          {remain < 0 ? (
            // Thiếu tiền: nút chuyển thành Nạp thêm như bản thiết kế
            <button
              type="button"
              className={clsx(btn, btnPrimary, btnLg, "w-full")}
              onClick={() => portalDialogRef.close({ topup: -remain })}
            >
              <IcWallet className="h-[18px] w-[18px]" />
              {t("Nạp thêm")} {formatMoneyVN(Math.ceil(-remain))} VND
            </button>
          ) : (
            <>
              <button
                type="button"
                className={clsx(btn, btnPrimary, btnLg, "w-full")}
                disabled={!!ruleError}
                onClick={() => portalDialogRef.close({ rentalPeriod: amount, unit })}
              >
                <IcClock className="h-[18px] w-[18px]" />
                {t("Gia hạn")} · {formatMoneyVN(newPrice)}đ
              </button>

              <p className="m-0 text-center text-[13px] text-muted">
                {t("Số dư còn lại sau gia hạn")}: <b className="text-fg">{formatMoneyVN(remain)} VND</b>
              </p>
            </>
          )}
          <p className="m-0 text-center text-xs text-faint">
            Lưu ý: Không hoàn tiền khi đã thuê máy. Nếu vi phạm{" "}
            <a className="font-semibold text-brand" href={PathnameUrl.TERMS} target="_blank" rel="noreferrer">
              điều khoản sử dụng
            </a>{" "}
            sẽ bị khoá tài khoản vĩnh viễn.
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default BootImageModal;
